<template>
    <div :class="['tuner-page', { 'tuner-embedded': embedded }]">
        <BackendHeader v-if="!embedded" />

        <div class="tuner-container">
            <div class="page-header">
                <h2>Deal Grade Tuner</h2>
                <div class="stats-bar" v-if="stats">
                    <span class="stat"><strong>{{ stats.totals?.labeled || 0 }}</strong> labeled</span>
                    <span class="stat agree"><strong>{{ agreementPct }}%</strong> agreement</span>
                    <span class="stat" v-for="t in stats.byTier" :key="t.modelTier">
                        {{ t.modelTier }}: {{ t.agreed }}/{{ t.labeled }}
                    </span>
                    <button @click="loadStats" class="button button-secondary">Refresh stats</button>
                </div>
            </div>

            <TrainNowBar
                v-if="!embedded"
                kind="price"
                :status="trainingStatus?.priceModel"
                :loading="trainingStatusLoading"
                :triggering="trainingTriggering"
                @train="onTrainClick"
                @refresh="loadTrainingStatus"
            />

            <div v-if="loading" class="loading-cell">Loading next deal…</div>
            <div v-else-if="done" class="empty-cell">
                🎉 No more unlabeled graded deals. Come back after more grading.
            </div>
            <div v-else-if="deal" class="deal-card">
                <div class="deal-top">
                    <div class="deal-image">
                        <ImagePreview v-if="firstImage" :imageUrl="firstImage" :maxWidth="260" :maxHeight="260" :finalWidth="600" :finalHeight="600" />
                        <div v-else class="no-image">no image</div>
                        <ImagePreview
                            v-if="keepaChartUrl"
                            :imageUrl="keepaChartUrl"
                            :maxWidth="260"
                            :maxHeight="180"
                            :finalWidth="700"
                            :finalHeight="500"
                        />
                    </div>

                    <div class="deal-info">
                        <h3 class="deal-title">
                            {{ deal.title || '(no title)' }}
                            <a v-if="deal.productLink" :href="deal.productLink" target="_blank" class="product-link" title="Open product page">↗</a>
                        </h3>
                        <div class="meta">
                            <span v-if="deal.brand" class="meta-item"><strong>Brand:</strong> {{ deal.brand }}</span>
                            <span v-if="deal.promoCode" class="meta-item"><strong>Code:</strong> {{ deal.promoCode }}</span>
                            <span v-if="dealTypeCode !== null" class="meta-item">
                                <strong>Type:</strong>
                                <span class="deal-type-badge" :class="`deal-type-${dealTypeClass}`">{{ dealTypeName }}</span>
                            </span>
                            <span v-if="scrapedLabel" class="meta-item" :title="deal.fetchedAt || deal.createdAt || ''">
                                <strong>Scraped:</strong> {{ scrapedLabel }}
                            </span>
                            <span v-if="stockLabel" class="meta-item" :title="stockTooltip">
                                <strong>Stock:</strong>
                                <span class="stock-badge" :class="stockBadgeClass">{{ stockLabel }}</span>
                            </span>
                        </div>
                        <p v-if="deal.description" class="desc">{{ truncate(deal.description, 280) }}</p>

                        <div v-if="deal.postText" class="post-block">
                            <div class="post-heading">
                                Original post
                                <span v-if="deal.postSource" class="post-source">· {{ deal.postSource }}</span>
                                <button class="post-expand" @click="openPostModal" type="button">view full</button>
                            </div>
                            <div class="post-preview" @click="openPostModal">
                                {{ truncate(deal.postText, 220) }}
                            </div>
                        </div>

                        <div class="prices">
                            <div class="price-box">
                                <span class="lbl">Model Retail</span>
                                <span class="val muted">{{ fmt(deal.retailPrice) }}</span>
                            </div>
                            <div class="price-box">
                                <span class="lbl">Model Sale</span>
                                <span class="val">{{ fmt(deal.salePrice) }}</span>
                            </div>
                            <div class="price-box">
                                <span class="lbl">Model Final</span>
                                <span class="val final">{{ fmt(deal.finalPrice) }}</span>
                            </div>
                            <a v-if="deal.productLink"
                               :href="deal.productLink"
                               target="_blank"
                               rel="noopener"
                               class="open-deal-btn">
                                Open Deal ↗
                            </a>
                        </div>

                        <div class="thresholds">
                            <div class="th-heading">
                                What price would make this a... (fill any one — trains the model):
                                <a v-if="deal.productLink" :href="deal.productLink" target="_blank" class="open-link">open product ↗</a>
                            </div>
                            <div class="th-inputs">
                                <label class="th-field th-great">
                                    <span>Great deal at</span>
                                    <input v-model="greatThreshold" type="text" inputmode="decimal" placeholder="$" />
                                </label>
                                <label class="th-field th-good">
                                    <span>Good deal at</span>
                                    <input v-model="goodThreshold" type="text" inputmode="decimal" placeholder="$" />
                                </label>
                                <label class="th-field th-sale">
                                    <span>Sale-priced at</span>
                                    <input v-model="saleThreshold" type="text" inputmode="decimal" placeholder="$" />
                                </label>
                            </div>
                            <input v-model="priceNotes" type="text" class="price-notes" placeholder="Pricing notes (optional)" />
                        </div>

                        <div class="model-says">
                            <div class="ms-heading">Model says:</div>
                            <span class="grade-badge" :class="`grade-${(deal.gaugeTier||'').toLowerCase()}`">
                                {{ deal.gaugeTier || '—' }}
                            </span>
                            <span class="ms-detail">
                                fair ≈ {{ fmt(deal.fairP50) }}
                                <span v-if="deal.fairP25 != null && deal.fairP75 != null" class="muted">
                                    (P25 {{ fmt(deal.fairP25) }} · P75 {{ fmt(deal.fairP75) }})
                                </span>
                                · ratio {{ deal.ratio != null ? Number(deal.ratio).toFixed(2) : '—' }}
                                <span v-if="deal.gaugeBasis" class="muted"> · via {{ deal.gaugeBasis }}</span>
                                <span v-if="deal.neighborCount" class="muted"> · {{ deal.neighborCount }} neighbors</span>
                            </span>
                        </div>

                        <div v-if="deal.anchorRationale" class="rationale">
                            <strong>Rationale:</strong> {{ deal.anchorRationale }}
                        </div>
                    </div>
                </div>

                <div class="label-bar">
                    <div class="prompt">Pick a <strong>price tier</strong> and an <strong>audience score</strong>. Submits when both are set.</div>

                    <div class="axis">
                        <div class="axis-label">
                            Audience score for moms
                            <span class="audience-current" v-if="audienceScoreSet">
                                = <strong>{{ audienceScore }}</strong>
                            </span>
                            <span class="audience-current muted" v-else>(tap to set)</span>
                        </div>
                        <input
                            type="range"
                            class="audience-slider"
                            min="0"
                            max="100"
                            step="20"
                            list="audience-stops"
                            :value="audienceScore"
                            :class="{ 'slider-set': audienceScoreSet }"
                            @input="onAudienceInput($event)"
                            @change="onAudienceInput($event)"
                            :disabled="submitting"
                        />
                        <datalist id="audience-stops">
                            <option value="0"></option>
                            <option value="20"></option>
                            <option value="40"></option>
                            <option value="60"></option>
                            <option value="80"></option>
                            <option value="100"></option>
                        </datalist>
                        <div class="audience-stops-labels">
                            <span @click="pickAudience(0)" :class="{ 'stop-active': audienceScoreSet && audienceScore === 0 }">0</span>
                            <span @click="pickAudience(20)" :class="{ 'stop-active': audienceScoreSet && audienceScore === 20 }">20</span>
                            <span @click="pickAudience(40)" :class="{ 'stop-active': audienceScoreSet && audienceScore === 40 }">40</span>
                            <span @click="pickAudience(60)" :class="{ 'stop-active': audienceScoreSet && audienceScore === 60 }">60</span>
                            <span @click="pickAudience(80)" :class="{ 'stop-active': audienceScoreSet && audienceScore === 80 }">80</span>
                            <span @click="pickAudience(100)" :class="{ 'stop-active': audienceScoreSet && audienceScore === 100 }">100</span>
                        </div>
                    </div>

                    <div class="axis">
                        <div class="axis-label">Price tier</div>
                        <div class="buttons">
                            <button class="tier-btn grade-great" @click="pickTier('Great')" :class="{ 'tier-selected': selectedTier === 'Great' }" :disabled="submitting">
                                <span class="key">1</span> Great
                            </button>
                            <button class="tier-btn grade-good" @click="pickTier('Good')" :class="{ 'tier-selected': selectedTier === 'Good' }" :disabled="submitting">
                                <span class="key">2</span> Good
                            </button>
                            <button class="tier-btn grade-sale" @click="pickTier('Sale')" :class="{ 'tier-selected': selectedTier === 'Sale' }" :disabled="submitting">
                                <span class="key">3</span> Sale
                            </button>
                            <button class="tier-btn grade-regular" @click="pickTier('Regular')" :class="{ 'tier-selected': selectedTier === 'Regular' }" :disabled="submitting">
                                <span class="key">4</span> Regular
                            </button>
                            <button class="tier-btn grade-notadeal" @click="pickTier('NotADeal')" :class="{ 'tier-selected': selectedTier === 'NotADeal' }" :disabled="submitting">
                                <span class="key">5</span> Not a Deal
                            </button>
                            <button class="tier-btn grade-bad" @click="pickTier('BadData')" :class="{ 'tier-selected': selectedTier === 'BadData' }" :disabled="submitting">
                                <span class="key">6</span> Bad Data
                            </button>
                        </div>
                    </div>

                    <input v-model="notes" type="text" placeholder="Optional notes (why?)" class="notes" />
                    <div class="shortcuts">
                        Keys: <kbd>1</kbd>=Great <kbd>2</kbd>=Good <kbd>3</kbd>=Sale <kbd>4</kbd>=Regular <kbd>5</kbd>=Not&nbsp;a&nbsp;Deal <kbd>6</kbd>=Bad&nbsp;Data · <kbd>Q</kbd>–<kbd>Y</kbd> = audience (0,20,40,60,80,100) · <kbd>N</kbd> = skip
                    </div>
                </div>
            </div>

            <div v-if="error" class="error-message">{{ error }}</div>
        </div>

        <div v-if="postModal.open" class="modal-overlay" @click.self="closePostModal">
            <div class="modal">
                <div class="modal-header">
                    <span>Original post<span v-if="postModal.source"> — {{ postModal.source }}</span></span>
                    <button @click="closePostModal" class="close-btn" type="button">&times;</button>
                </div>
                <div class="modal-body modal-body-text">
                    <pre class="text-pre">{{ postModal.text }}</pre>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BackendHeader from '@/components/BackendHeader.vue'
import ImagePreview from '@/components/Backend/ImagePreview.vue'
import TrainNowBar from '@/components/TrainNowBar.vue'
import { MODAL_CLOSE } from '@/services/modal'
import api from '@/api'

const props = defineProps<{
    dealId?: number
    embedded?: boolean
}>()
const emit = defineEmits<{
    (e: 'graded'): void
}>()
const closeModal = inject(MODAL_CLOSE, undefined)

interface TunerDeal {
    dealId: number
    title: string | null
    brand: string | null
    description: string | null
    retailPrice: number | null
    salePrice: number | null
    finalPrice: number | null
    imagesJson: string | null
    promoCode: string | null
    couponText: string | null
    productLink: string | null
    fetchedAt: string | null
    createdAt: string | null
    postText: string | null
    postSource: string | null
    available: number | null
    availabilityMessage: string | null
    availabilityType: string | null
    availabilitySource: string | null
    finalDealTypeCode: number | null
    topDealTypeCode: number | null
    gaugeTier: string | null
    gaugeBasis: string | null
    score: number | null
    fairP25: number | null
    fairP50: number | null
    fairP75: number | null
    ratio: number | null
    neighborCount: number | null
    anchorRationale: string | null
}

const route = useRoute()
const router = useRouter()
const deal = ref<TunerDeal | null>(null)
const loading = ref(false)
const submitting = ref(false)
const done = ref(false)
const error = ref<string | null>(null)
const notes = ref('')
const priceNotes = ref('')
const realRetail = ref<string>('')
const realSale = ref<string>('')
const realFinal = ref<string>('')
const stats = ref<any>(null)
const postModal = ref({ open: false, text: '', source: '' })

// Tier-anchored price thresholds — user enters "this would be a Great deal at $X"
// and the backend derives an implied fairP50 to feed into GradeCorrections.
const greatThreshold = ref<string>('')
const goodThreshold = ref<string>('')
const saleThreshold = ref<string>('')

// Two-axis labeling: user selects both a price tier AND an audience score.
// We auto-submit once both are set. audienceScoreSet distinguishes "user picked 60"
// from "default 60" — only true after the user actually moved the slider/clicked a stop.
const selectedTier = ref<string | null>(null)
const audienceScore = ref<number>(60)
const audienceScoreSet = ref<boolean>(false)

const DEAL_TYPE_NAMES: Record<number, string> = {
    0: 'Unknown', 1: 'Sale', 2: 'Coupon', 3: 'Promo',
    4: 'Sale + Promo', 5: 'Sale + Coupon', 6: 'Sale + Coupon + Promo',
    7: 'Coupon + Promo', 8: 'Good Price'
}
const DEAL_TYPE_CLASSES: Record<number, string> = {
    0: 'unknown', 1: 'sale-price-deal', 2: 'coupon-deal', 3: 'promo-code-deal',
    4: 'sale-price-with-promo-code', 5: 'sale-price-with-coupon',
    6: 'sale-price-with-coupon-and-promo-code', 7: 'coupon-and-promo-code-deal',
    8: 'good-price-deal'
}
const dealTypeCode = computed<number | null>(() => {
    const d = deal.value
    if (!d) return null
    if (d.finalDealTypeCode != null) return d.finalDealTypeCode
    if (d.topDealTypeCode != null) return d.topDealTypeCode
    return null
})
const dealTypeName = computed(() => DEAL_TYPE_NAMES[dealTypeCode.value ?? 0] ?? 'Unknown')
const dealTypeClass = computed(() => DEAL_TYPE_CLASSES[dealTypeCode.value ?? 0] ?? 'unknown')

const parseNum = (s: string): number | undefined => {
    if (!s) return undefined
    const n = parseFloat(s.replace(/[^0-9.\-]/g, ''))
    return isNaN(n) ? undefined : n
}

const stockLabel = computed(() => {
    const d = deal.value
    if (!d) return ''
    if (d.available != null && d.available > 0) return `${d.available} left`
    if (d.availabilityType && /out|unavail/i.test(d.availabilityType)) return 'Out of stock'
    if (d.availabilityMessage) {
        const m = d.availabilityMessage.trim()
        return m.length > 24 ? m.slice(0, 24) + '…' : m
    }
    return ''
})
const stockBadgeClass = computed(() => {
    const d = deal.value
    if (!d) return ''
    if (d.availabilityType && /out|unavail/i.test(d.availabilityType)) return 'stock-out'
    if (d.available != null && d.available <= 3) return 'stock-low'
    if (d.available != null && d.available <= 10) return 'stock-med'
    return 'stock-ok'
})
const stockTooltip = computed(() => {
    const d = deal.value
    if (!d) return ''
    const parts: string[] = []
    if (d.available != null) parts.push(`max qty ${d.available}`)
    if (d.availabilityMessage) parts.push(d.availabilityMessage)
    if (d.availabilityType) parts.push(`type: ${d.availabilityType}`)
    if (d.availabilitySource) parts.push(`source: ${d.availabilitySource}`)
    return parts.join(' · ')
})

const scrapedLabel = computed(() => {
    let iso = deal.value?.fetchedAt || deal.value?.createdAt
    if (!iso) return ''
    // Backend stores UTC but JSON omits the Z — force UTC parsing.
    const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso)
    const d = new Date(hasTz ? iso : iso + 'Z')
    if (isNaN(d.getTime())) return ''
    const diffMs = Date.now() - d.getTime()
    const mins = Math.round(diffMs / 60000)
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.round(mins / 60)
    if (hrs < 24) return `${hrs}h ago`
    const days = Math.round(hrs / 24)
    if (days < 30) return `${days}d ago`
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
})

const firstImage = computed(() => {
    const j = deal.value?.imagesJson
    if (!j) return null
    try {
        const arr = JSON.parse(j)
        if (Array.isArray(arr) && arr.length > 0) return arr[0]
        if (typeof arr === 'string') return arr
    } catch { /* ignore */ }
    return null
})

const keepaChartUrl = computed(() => {
    const link = deal.value?.productLink
    if (!link) return null
    const m = link.match(/\/(?:dp|gp\/product|product)\/(B0[A-Z0-9]{8}|\d{10})/i)
    if (!m) return null
    return `https://graph.keepa.com/pricehistory.png?asin=${m[1].toUpperCase()}&domain=com&bb=1&new=1&range=90`
})

const agreementPct = computed(() => {
    const t = stats.value?.totals
    if (!t || !t.labeled) return 0
    return Math.round((t.agreed / t.labeled) * 100)
})

const fmt = (v: number | null | undefined) => v != null ? '$' + Number(v).toFixed(2) : '—'
const truncate = (s: string, n: number) => s && s.length > n ? s.slice(0, n) + '…' : s

const loadNext = async (specificDealId?: number) => {
    loading.value = true
    error.value = null
    try {
        const resp = await api.getNextTunerDeal(specificDealId)
        if (resp.data?.done) {
            done.value = true
            deal.value = null
        } else {
            done.value = false
            deal.value = resp.data
            notes.value = ''
            priceNotes.value = ''
            realRetail.value = ''
            realSale.value = ''
            realFinal.value = ''
            greatThreshold.value = ''
            goodThreshold.value = ''
            saleThreshold.value = ''
            // Reset two-axis selections for the new deal
            selectedTier.value = null
            audienceScore.value = 60
            audienceScoreSet.value = false
        }
    } catch (e: any) {
        error.value = e?.response?.data?.error || e?.message || 'Failed to load deal'
    } finally {
        loading.value = false
    }
}

// Manual-training-now state. Same shape as on AudienceTuner; the bar shows
// "X corrections ready / last trained Nm ago" and a Train Now button that
// hits the price-model retrain endpoint.
const trainingStatus = ref<any>(null)
const trainingStatusLoading = ref(false)
const trainingTriggering = ref(false)
let trainingPollTimer: ReturnType<typeof setInterval> | null = null

const loadTrainingStatus = async () => {
    trainingStatusLoading.value = true
    try {
        const resp = await api.getTrainingStatus()
        trainingStatus.value = resp.data
    } catch { /* keep last value */ }
    finally { trainingStatusLoading.value = false }
}

const onTrainClick = async () => {
    if (trainingTriggering.value) return
    trainingTriggering.value = true
    try {
        await api.trainPriceModel()
        if (trainingPollTimer) clearInterval(trainingPollTimer)
        trainingPollTimer = setInterval(async () => {
            await loadTrainingStatus()
            if (trainingStatus.value?.priceModel?.status !== 'training') {
                if (trainingPollTimer) { clearInterval(trainingPollTimer); trainingPollTimer = null }
            }
        }, 5000)
        await loadTrainingStatus()
    } catch (e) {
        console.error('Trigger train failed', e)
    } finally {
        trainingTriggering.value = false
    }
}

const loadStats = async () => {
    try {
        const resp = await api.getTunerStats()
        stats.value = resp.data
    } catch { /* non-fatal */ }
}

const pickTier = (tier: string) => {
    if (submitting.value) return
    selectedTier.value = tier
    trySubmit()
}

const pickAudience = (score: number) => {
    if (submitting.value) return
    audienceScore.value = score
    audienceScoreSet.value = true
    trySubmit()
}

const onAudienceInput = (e: Event) => {
    const v = Number((e.target as HTMLInputElement).value)
    if (isNaN(v)) return
    pickAudience(v)
}

const trySubmit = async () => {
    if (!deal.value || submitting.value) return
    if (!selectedTier.value || !audienceScoreSet.value) return
    submitting.value = true
    error.value = null
    try {
        if (!props.embedded && route.query.dealId) router.replace({ query: {} })
        // BadData → only save the price label; audience score makes no sense for junk data.
        const tasks: Promise<any>[] = [
            api.saveTunerLabel({
                dealId: deal.value.dealId,
                yourTier: selectedTier.value,
                notes: notes.value || undefined,
                priceNotes: priceNotes.value || undefined,
                realRetailPrice: parseNum(realRetail.value),
                realSalePrice: parseNum(realSale.value),
                realFinalPrice: parseNum(realFinal.value),
                greatThreshold: parseNum(greatThreshold.value),
                goodThreshold: parseNum(goodThreshold.value),
                saleThreshold: parseNum(saleThreshold.value)
            })
        ]
        if (selectedTier.value !== 'BadData') {
            tasks.push(api.saveAudienceLabel({
                dealId: deal.value.dealId,
                yourScore: audienceScore.value
            }))
        }
        await Promise.all(tasks)
        await loadStats()
        emit('graded')
        if (closeModal) closeModal({ graded: true, dealId: deal.value?.dealId })
        else if (!props.embedded) await loadNext()
    } catch (e: any) {
        error.value = e?.response?.data?.error || e?.message || 'Failed to save label'
    } finally {
        submitting.value = false
    }
}

const openPostModal = () => {
    if (!deal.value) return
    postModal.value = { open: true, text: deal.value.postText || '', source: deal.value.postSource || '' }
}
const closePostModal = () => { postModal.value.open = false }

const onKeyDown = (e: KeyboardEvent) => {
    if (postModal.value.open) {
        if (e.key === 'Escape') closePostModal()
        return
    }
    if (submitting.value || loading.value) return
    const target = e.target as HTMLElement | null
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        if (e.key === 'Enter') {
            // Enter from the notes field: do nothing — user must click a tier
            return
        }
        return
    }
    const k = e.key.toLowerCase()
    if (k === '1') pickTier('Great')
    else if (k === '2') pickTier('Good')
    else if (k === '3') pickTier('Sale')
    else if (k === '4') pickTier('Regular')
    else if (k === '5') pickTier('NotADeal')
    else if (k === '6') pickTier('BadData')
    // Audience score on the QWERTY row: q=0, w=20, e=40, r=60, t=80, y=100.
    else if (k === 'q') pickAudience(0)
    else if (k === 'w') pickAudience(20)
    else if (k === 'e') pickAudience(40)
    else if (k === 'r') pickAudience(60)
    else if (k === 't') pickAudience(80)
    else if (k === 'y') pickAudience(100)
    else if (k === 'n') loadNext()
}

onMounted(() => {
    loadStats()
    if (!props.embedded) loadTrainingStatus()
    const initialId = props.dealId ?? (route.query.dealId ? Number(route.query.dealId) : undefined)
    loadNext(initialId && !isNaN(initialId) ? initialId : undefined)
    window.addEventListener('keydown', onKeyDown)
})
onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeyDown)
    if (trainingPollTimer) clearInterval(trainingPollTimer)
})
</script>

<style scoped>
.tuner-page { min-height: 100vh; background: #f5f5f5; padding: 20px; text-align: left; }
.tuner-embedded { min-height: unset; background: transparent; padding: 0; }
.tuner-embedded .tuner-container { box-shadow: none; border-radius: 0; max-width: none; }
.tuner-container { max-width: 1100px; margin: 0 auto; background: #fff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); padding: 20px; }

.page-header { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #e0e0e0; flex-wrap: wrap; }
.page-header h2 { margin: 0; color: #333; }

.stats-bar { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; font-size: 13px; color: #555; }
.stat { padding: 4px 10px; background: #eceff1; border-radius: 12px; }
.stat.agree { background: #dcfce7; color: #166534; }

.deal-card { border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden; }
.deal-top { display: flex; gap: 20px; padding: 20px; }
.deal-image { flex: 0 0 260px; display: flex; flex-direction: column; align-items: center; gap: 10px; }
.no-image { width: 260px; height: 260px; background: #f5f5f5; display: flex; align-items: center; justify-content: center; color: #aaa; border-radius: 4px; }
.deal-info { flex: 1; min-width: 0; }
.deal-title { margin: 0 0 8px 0; font-size: 18px; line-height: 1.3; color: #222; }
.meta { display: flex; gap: 14px; flex-wrap: wrap; font-size: 13px; color: #666; margin-bottom: 8px; }
.desc { margin: 0 0 14px 0; font-size: 13px; color: #555; line-height: 1.5; }

.prices { display: flex; gap: 10px; margin-bottom: 14px; }
.price-box { background: #fafafa; border: 1px solid #eee; border-radius: 6px; padding: 8px 14px; display: flex; flex-direction: column; min-width: 90px; }
.price-box .lbl { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; }
.price-box .val { font-size: 18px; font-weight: 600; color: #333; }
.price-box .val.final { color: #2e7d32; }
.price-box .val.muted { color: #bbb; text-decoration: line-through; font-weight: 500; }

.model-says { background: #f6f8fa; border-radius: 6px; padding: 10px 14px; margin-bottom: 10px; font-size: 13px; }
.ms-heading { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.ms-detail { margin-left: 8px; color: #555; }
.muted { color: #999; }
.rationale { font-size: 12px; color: #666; font-style: italic; padding: 6px 10px; background: #fafafa; border-left: 3px solid #cfd8dc; border-radius: 2px; }

.grade-badge { display: inline-block; padding: 3px 10px; border-radius: 12px; font-size: 12px; font-weight: 700; letter-spacing: 0.3px; }
.grade-great { background: #2e7d32; color: #fff; }
.grade-good { background: #66bb6a; color: #fff; }
.grade-sale { background: #fff3c4; color: #8a6d00; }
.grade-regular { background: #eceff1; color: #555; }
.grade-notadeal { background: #d32f2f; color: #fff; }

.label-bar { border-top: 1px solid #eee; padding: 20px; background: #fafafa; }
.prompt { font-size: 15px; color: #333; margin-bottom: 14px; }
.prompt strong { color: #1565c0; }

.axis { margin-bottom: 16px; }
.axis-label { font-size: 12px; color: #555; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; font-weight: 600; }
.audience-current { text-transform: none; letter-spacing: normal; font-weight: 500; color: #1565c0; margin-left: 8px; font-size: 13px; }
.audience-current.muted { color: #999; font-style: italic; }
.audience-current strong { color: #1565c0; font-size: 16px; }

.buttons { display: flex; gap: 10px; flex-wrap: wrap; }
.tier-btn { padding: 12px 18px; border: 2px solid transparent; border-radius: 8px; font-size: 14px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 8px; transition: transform 0.05s ease, box-shadow 0.2s, outline-color 0.15s; min-width: 130px; justify-content: center; outline: 3px solid transparent; outline-offset: 2px; }
.tier-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 8px rgba(0,0,0,0.1); }
.tier-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.tier-btn.tier-selected { outline-color: #1565c0; box-shadow: 0 4px 12px rgba(21,101,192,0.25); }
.tier-btn .key { background: rgba(0,0,0,0.15); color: inherit; font-size: 11px; padding: 2px 6px; border-radius: 3px; }
.tier-btn.grade-great { background: #2e7d32; color: #fff; }
.tier-btn.grade-good { background: #66bb6a; color: #fff; }
.tier-btn.grade-sale { background: #fff3c4; color: #8a6d00; }
.tier-btn.grade-regular { background: #cfd8dc; color: #37474f; }
.tier-btn.grade-notadeal { background: #d32f2f; color: #fff; }
.tier-btn.grade-bad { background: #ef9a9a; color: #5a0000; }

.audience-slider { width: 100%; height: 36px; -webkit-appearance: none; appearance: none; background: linear-gradient(90deg, #ef9a9a 0%, #fff3c4 50%, #66bb6a 100%); border-radius: 18px; outline: none; opacity: 0.55; transition: opacity 0.15s; cursor: pointer; }
.audience-slider.slider-set { opacity: 1; }
.audience-slider:disabled { opacity: 0.3; cursor: not-allowed; }
.audience-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 32px; height: 32px; border-radius: 50%; background: #fff; border: 3px solid #1565c0; cursor: pointer; box-shadow: 0 2px 6px rgba(0,0,0,0.25); }
.audience-slider::-moz-range-thumb { width: 32px; height: 32px; border-radius: 50%; background: #fff; border: 3px solid #1565c0; cursor: pointer; box-shadow: 0 2px 6px rgba(0,0,0,0.25); }
.audience-stops-labels { display: flex; justify-content: space-between; padding: 0 8px; margin-top: 4px; }
.audience-stops-labels span { font-size: 13px; color: #777; cursor: pointer; padding: 4px 8px; border-radius: 4px; min-width: 32px; text-align: center; font-weight: 600; user-select: none; }
.audience-stops-labels span:hover { background: #e3f2fd; color: #1565c0; }
.audience-stops-labels span.stop-active { background: #1565c0; color: #fff; }

.notes { width: 100%; padding: 8px 12px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px; }
.shortcuts { font-size: 11px; color: #888; margin-top: 8px; }
.shortcuts kbd { background: #eceff1; padding: 1px 6px; border-radius: 3px; font-size: 11px; font-family: ui-monospace, monospace; border: 1px solid #cfd8dc; }

.product-link { margin-left: 8px; color: #1565c0; text-decoration: none; font-weight: 600; }
.product-link:hover { color: #0d47a1; }

.deal-type-badge { display: inline-block; padding: 2px 8px; border-radius: 6px; font-weight: 600; font-size: 11px; margin-left: 4px; }
.deal-type-badge.deal-type-unknown { background: #f1f5f9; color: #475569; }
.deal-type-badge.deal-type-sale-price-deal { background: #e0f2fe; color: #075985; }
.deal-type-badge.deal-type-coupon-deal { background: #fff7ed; color: #9a3412; }
.deal-type-badge.deal-type-promo-code-deal { background: #f5f3ff; color: #5b21b6; }
.deal-type-badge.deal-type-sale-price-with-promo-code { background: #dcfce7; color: #166534; }
.deal-type-badge.deal-type-sale-price-with-coupon { background: #fef3c7; color: #92400e; }
.deal-type-badge.deal-type-sale-price-with-coupon-and-promo-code { background: #fce7f3; color: #9d174d; }
.deal-type-badge.deal-type-coupon-and-promo-code-deal { background: #ede9fe; color: #6d28d9; }
.deal-type-badge.deal-type-good-price-deal { background: #ecfccb; color: #3f6212; }

.stock-badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-weight: 700; font-size: 11px; margin-left: 4px; }
.stock-ok   { background: #e8f5e9; color: #2e7d32; }
.stock-med  { background: #fff3c4; color: #8a6d00; }
.stock-low  { background: #ffe0b2; color: #b45309; }
.stock-out  { background: #ffebee; color: #c62828; }

.post-block { background: #f6f8fa; border-radius: 6px; padding: 8px 12px; margin-bottom: 12px; }
.post-heading { font-size: 11px; color: #666; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; display: flex; align-items: center; gap: 6px; }
.post-source { text-transform: none; letter-spacing: normal; color: #999; font-weight: 500; }
.post-expand { margin-left: auto; font-size: 11px; background: #eceff1; color: #333; border: none; padding: 2px 8px; border-radius: 3px; cursor: pointer; }
.post-expand:hover { background: #cfd8dc; }
.post-preview { font-size: 12px; color: #444; line-height: 1.5; white-space: pre-wrap; cursor: pointer; overflow-wrap: anywhere; }
.post-preview:hover { color: #1565c0; }

.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal { background: #fff; border-radius: 8px; width: 90vw; max-width: 800px; max-height: 85vh; display: flex; flex-direction: column; box-shadow: 0 8px 32px rgba(0,0,0,0.25); }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 18px; border-bottom: 1px solid #e0e0e0; font-weight: 600; color: #333; }
.close-btn { background: none; border: none; font-size: 22px; cursor: pointer; color: #666; line-height: 1; padding: 0 4px; }
.close-btn:hover { color: #c62828; }
.modal-body { flex: 1; overflow: auto; padding: 16px 18px; background: #fafafa; border-radius: 0 0 8px 8px; }
.text-pre { margin: 0; color: #333; font-family: inherit; font-size: 14px; white-space: pre-wrap; word-wrap: break-word; line-height: 1.7; }

.real-prices { background: #fff8e1; border: 1px dashed #f0c36d; border-radius: 6px; padding: 10px 14px; margin-bottom: 14px; }
.rp-heading { font-size: 12px; color: #7a5a00; margin-bottom: 8px; font-weight: 600; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; }

.thresholds { background: #f0f7ff; border: 1px dashed #8ab4f0; border-radius: 6px; padding: 10px 14px; margin-bottom: 14px; }
.th-heading { font-size: 12px; color: #1565c0; margin-bottom: 8px; font-weight: 600; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; }
.th-inputs { display: flex; gap: 10px; margin-bottom: 8px; flex-wrap: wrap; }
.th-field { display: flex; flex-direction: column; flex: 1; min-width: 100px; }
.th-field span { font-size: 10px; color: #555; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 2px; font-weight: 700; }
.th-field input { padding: 8px 10px; border: 2px solid; border-radius: 4px; font-size: 14px; font-weight: 600; background: #fff; }
.th-field input:focus { outline: none; box-shadow: 0 0 0 3px rgba(21,101,192,0.2); }
.th-field.th-great input { border-color: #2e7d32; }
.th-field.th-great span { color: #2e7d32; }
.th-field.th-good input { border-color: #66bb6a; }
.th-field.th-good span { color: #2e7d32; }
.th-field.th-sale input { border-color: #c79100; }
.th-field.th-sale span { color: #8a6d00; }
.open-link { color: #1565c0; font-weight: 500; font-size: 12px; text-decoration: none; }
.open-link:hover { text-decoration: underline; }
.open-deal-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 16px;
    background: #1565c0;
    color: #fff;
    text-decoration: none;
    border-radius: 6px;
    font-weight: 600;
    font-size: 13px;
    white-space: nowrap;
    transition: background 0.15s;
    margin-left: auto;
}
.open-deal-btn:hover { background: #0d47a1; }
.rp-inputs { display: flex; gap: 10px; margin-bottom: 8px; flex-wrap: wrap; }
.rp-field { display: flex; flex-direction: column; flex: 1; min-width: 100px; }
.rp-field span { font-size: 10px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 2px; }
.rp-field input { padding: 6px 10px; border: 1px solid #e0c380; border-radius: 4px; font-size: 14px; font-weight: 600; background: #fff; }
.rp-field input:focus { outline: none; border-color: #c79100; }
.price-notes { width: 100%; padding: 6px 10px; border: 1px solid #e0c380; border-radius: 4px; font-size: 12px; background: #fff; }

.loading-cell, .empty-cell { text-align: center; padding: 60px; color: #999; font-size: 14px; }
.error-message { color: #c62828; background: #ffebee; border-radius: 4px; padding: 12px; margin-top: 12px; }

.button { padding: 6px 12px; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; }
.button-secondary { background: #eceff1; color: #333; }
.button-secondary:hover { background: #cfd8dc; }

/* Compact overrides for the modal-embedded variant — fits the whole tuner
   (including label-bar buttons) inside ModalService's 90vh without scrolling. */
.tuner-embedded .tuner-container { padding: 10px 14px; }
.tuner-embedded .page-header { margin-bottom: 8px; padding-bottom: 6px; }
.tuner-embedded .page-header h2 { font-size: 16px; }

.tuner-embedded .deal-top { gap: 14px; padding: 10px 14px; }
.tuner-embedded .deal-image { flex: 0 0 180px; }
.tuner-embedded .no-image { width: 180px; height: 180px; }

.tuner-embedded .deal-title { font-size: 14px; margin-bottom: 4px; }
.tuner-embedded .meta { font-size: 12px; gap: 10px; margin-bottom: 4px; }
.tuner-embedded .desc { font-size: 12px; line-height: 1.4; margin-bottom: 6px; }

.tuner-embedded .post-block { padding: 6px 10px; margin-bottom: 6px; }
.tuner-embedded .post-preview { font-size: 11px; line-height: 1.4; max-height: 3.6em; overflow: hidden; }

.tuner-embedded .prices { gap: 6px; margin-bottom: 6px; }
.tuner-embedded .price-box { padding: 4px 10px; min-width: 70px; }
.tuner-embedded .price-box .val { font-size: 14px; }

.tuner-embedded .real-prices { padding: 6px 10px; margin-bottom: 6px; }
.tuner-embedded .rp-heading { margin-bottom: 4px; }
.tuner-embedded .rp-inputs { gap: 6px; margin-bottom: 4px; }
.tuner-embedded .rp-field input { padding: 4px 8px; font-size: 13px; }
.tuner-embedded .price-notes { padding: 4px 8px; }

.tuner-embedded .model-says { padding: 6px 10px; margin-bottom: 4px; font-size: 12px; }
.tuner-embedded .rationale { padding: 4px 8px; font-size: 11px; }

.tuner-embedded .label-bar { padding: 10px 14px; }
.tuner-embedded .prompt { font-size: 13px; margin-bottom: 6px; }
.tuner-embedded .axis { margin-bottom: 8px; }
.tuner-embedded .axis-label { font-size: 11px; margin-bottom: 4px; }
.tuner-embedded .buttons { gap: 6px; }
.tuner-embedded .tier-btn { padding: 6px 12px; font-size: 13px; min-width: 100px; }
.tuner-embedded .audience-slider { height: 28px; }
.tuner-embedded .audience-stops-labels span { font-size: 11px; padding: 2px 6px; }
.tuner-embedded .notes { padding: 5px 10px; font-size: 12px; }
.tuner-embedded .shortcuts { margin-top: 4px; }

/* Mobile (≤768px): stack everything, larger tap targets, hide non-essential clutter
   so the deal title + image + grading controls fit one screen. */
@media (max-width: 768px) {
    .tuner-page { padding: 8px; }
    .tuner-container { padding: 10px; border-radius: 4px; }
    .page-header { margin-bottom: 10px; padding-bottom: 8px; }
    .page-header h2 { font-size: 16px; }
    .stats-bar { font-size: 11px; gap: 6px; }
    .stat { padding: 2px 6px; }

    .deal-top { flex-direction: column; gap: 10px; padding: 10px; }
    .deal-image { flex: 0 0 auto; }
    .no-image { width: 100%; height: 180px; }
    .deal-image :deep(img) { max-width: 100% !important; max-height: 200px !important; }

    .deal-title { font-size: 16px; }
    .meta { gap: 8px; font-size: 12px; }
    .desc { font-size: 12px; line-height: 1.4; }

    .post-block { padding: 6px 10px; }
    .post-preview { font-size: 11px; max-height: 4em; overflow: hidden; }

    .prices { flex-wrap: wrap; gap: 6px; }
    .price-box { padding: 6px 10px; min-width: 0; flex: 1 1 30%; }
    .price-box .val { font-size: 14px; }
    .open-deal-btn { flex: 1 1 100%; padding: 10px 12px; margin: 6px 0 0 0; }

    .real-prices { padding: 8px 10px; }
    .rp-inputs { gap: 6px; }
    .rp-field { min-width: 0; flex: 1 1 30%; }
    .rp-field input { padding: 8px 8px; font-size: 14px; }

    .thresholds { padding: 8px 10px; }
    .th-inputs { gap: 6px; }
    .th-field { min-width: 0; flex: 1 1 30%; }
    .th-field input { padding: 10px 8px; font-size: 16px; min-height: 44px; }

    .label-bar { padding: 12px; }
    .prompt { font-size: 13px; margin-bottom: 10px; }

    /* Price tier: 2 rows of buttons with tappable size. */
    .buttons { gap: 6px; }
    .tier-btn {
        flex: 1 1 calc(50% - 4px);
        min-width: 0;
        padding: 14px 8px;
        font-size: 13px;
        min-height: 48px; /* 44px+ tap-target guideline */
    }
    .tier-btn .key { font-size: 10px; }

    /* Audience slider: bigger thumb, wider stops for thumbs. */
    .audience-slider { height: 44px; border-radius: 22px; }
    .audience-slider::-webkit-slider-thumb { width: 40px; height: 40px; }
    .audience-slider::-moz-range-thumb { width: 40px; height: 40px; }
    .audience-stops-labels span {
        font-size: 13px;
        padding: 8px 6px;
        min-width: 40px;
        min-height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .shortcuts { display: none; } /* keyboard shortcuts hint isn't useful on mobile */
}
</style>
